// Membership and How creators earn: one copy for the website (pages/membership.ts) and the launcher
// (launcher/src/ui/membership.rs reads this file with include_str! and parses the object as JSON), so keep the object
// strict JSON: double quotes, no trailing commas, no comments inside it. Numbers must match docs/PLAN.md "Three tiers"
// and money's config (test/membership.test.ts checks). A perk is "live" only once it exists; the rest say "Coming";
// "with_billing" perks are live whenever memberships are open (they exist as soon as billing does).
export const COPY = {
  "membership": {
    "eyebrow": "Coming later",
    "title": "Your monthly fee goes to the games you play.",
    "lead": "A membership is shared only among the games you played that month, by how long you actually played them. Creators get 80% of it after tax and payment fees in the first year.",
    "closed": {
      "title": "Not open yet",
      "body": "Everything on Banger is free right now. Membership opens later, once there's enough here to be worth paying for."
    },
    "shared_title": "How your fee is shared",
    "shared": [
      {
        "title": "Only what you play",
        "body": "Your fee is split among the games you played that month and nobody else's. Play one game all month and it gets all of it."
      },
      {
        "title": "Real play counts",
        "body": "Only time with the game window focused and you at the controls, up to a daily limit per game. Part of each fee is shared evenly across every game you played properly, so short games get their share too."
      },
      {
        "title": "Quiet months",
        "body": "If you don't play anything that month, your fee goes to that month's most-played games."
      }
    ],
    "split": {
      "creators": "80% to the creators of the games you played",
      "banger": "20% Banger",
      "note": "Of what's left after tax and payment fees, in the first year. Games that haven't been claimed by their creator don't get a share. Counting playtime is on by default and you can switch it off in Settings."
    },
    "tiers_title": "Three ways to be here",
    "tiers": [
      {
        "id": "free",
        "name": "Free",
        "price": "$0",
        "who": "Everyone 13+",
        "perks": [
          {
            "text": "Every game on Banger, free to play",
            "live": true
          },
          {
            "text": "Friends, parties and the Plaza",
            "live": true
          },
          {
            "text": "Clips of your last 30 seconds",
            "live": true
          },
          {
            "text": "Publish, claim and sell your work for free",
            "live": true
          },
          {
            "text": "Basic stats for your games: players, sessions, who comes back",
            "live": true
          }
        ]
      },
      {
        "id": "member",
        "name": "Member",
        "price": "$7.99 / €7.99 a month",
        "who": "Players 13+",
        "perks": [
          {
            "text": "Your fee goes to the games you play",
            "live": false,
            "with_billing": true
          },
          {
            "text": "A creator-made cosmetic every month, bought from its creator",
            "live": false
          },
          {
            "text": "A member badge on your profile and in the Plaza, and member emotes",
            "live": false
          },
          {
            "text": "Early access to coming-soon games",
            "live": false
          },
          {
            "text": "2-minute clips instead of 30 seconds",
            "live": false
          }
        ],
        "amounts": {
          "USD": 799,
          "EUR": 799
        }
      },
      {
        "id": "pro",
        "name": "Creator Pro",
        "price": "$14.99 / €14.99 a month",
        "who": "Creators 18+",
        "perks": [
          {
            "text": "Everything in Member (that part of your fee goes to the games you play)",
            "live": false,
            "with_billing": true
          },
          {
            "text": "Playtest replay: watch opted-in players' sessions to see where they get stuck",
            "live": false
          },
          {
            "text": "Where players quit, and heatmaps",
            "live": false
          },
          {
            "text": "Long history and exports",
            "live": false
          },
          {
            "text": "Bigger hosting limits for thrdm deploy",
            "live": false
          }
        ],
        "amounts": {
          "USD": 1499,
          "EUR": 1499
        }
      }
    ],
    "promises": "Publishing and selling are always free. Nobody can pay for a spot on Banger's rows. No ads, for anyone.",
    "coming": "Coming",
    "earn_link": "How creators earn",
    "open": {
      "eyebrow": "Banger membership",
      "title": "Join",
      "join": "Join for {price} a month",
      "sign_in": "Sign in to join",
      "pay_in": "Pay in",
      "card_note": "Billed monthly by card through Stripe, tax included. Cancel any time from Manage.",
      "pro_age": "Creator Pro is for creators 18 and over.",
      "active": "You're a {tier}.",
      "renews": "Renews {date}.",
      "ends": "Ends {date}.",
      "past_due": "Your last payment didn't go through. Update your card in Manage and we'll try again.",
      "pending": "Finishing your checkout. This page updates once Stripe confirms it.",
      "manage": "Manage membership",
      "opening": "Opening the checkout…",
      "error": "We couldn't start the checkout. Please try again in a moment."
    }
  },
  "earn": {
    "banner": "Selling is off for now: everything on Banger is free. This is how money will work when it switches on.",
    "eyebrow": "For players and creators",
    "title": "Where your money goes",
    "lead_on": "When you spend on Banger, most of it goes to the person who made the thing. Creators keep 80% of every sale after tax; Banger keeps 20%.",
    "lead_off": "When you spend on Banger, most of it will go to the person who made the thing. Creators will keep 80% of every sale after tax; Banger keeps 20%.",
    "table_title": "How creators earn",
    "head": [
      "When a player…",
      "The creator keeps",
      "Banger keeps"
    ],
    "rows": [
      [
        "buys an item in a game (extra lives, levels, skins)",
        "80%",
        "20%"
      ],
      [
        "buys an asset in the Workshop",
        "80%",
        "20%"
      ],
      [
        "buys a cosmetic to wear",
        "75% to its maker, 5% to the game it was bought in",
        "20%"
      ],
      [
        "tips a creator",
        "80%",
        "20%"
      ],
      [
        "is a member and plays the game",
        "80% of their fee, shared by playtime",
        "20%"
      ]
    ],
    "table_note": "Percentages are of the price after tax (and, for memberships, payment fees). Membership: 80% in the first year.",
    "balance": {
      "title": "Your Banger balance",
      "body": "Add money from $5 or €5 and spend it anywhere on Banger in one click. It stays on Banger: it can't be cashed out or sent to other players. You can get the unspent part of a top-up back within 14 days. Under 18, spending is limited to $50 a month. No loot boxes, ever."
    },
    "paid": {
      "title": "Getting paid",
      "body": "Creators are 18 or over. Each sale is held for 14 days, the refund window, then you can cash out from $10 through Stripe after a one-time ID check. Anything over $10 also goes out on the 1st of each month."
    },
    "dashboard": "Creator dashboard",
    "membership_link": "Membership and Creator Pro"
  }
};
